import { useMemo } from "react";
import { useAppSelector } from "@/core/store";
import { selectPointId } from "@/core/app/selectors";
import { useGuestOptionGroups, useGuestOptions } from "@/core/menu/guest-hooks";
import { resolveProductOptions, hasRequiredBlock } from "@/core/options";
import type { VisibleBlock } from "@/core/options";
import type { MenuProduct } from "@/types";

export interface UseProductOptionBlocksResult {
  blocks: VisibleBlock[];
  /**
   * «Про блоки этого товара пока ничего не известно» — держать добавление
   * закрытым. Не то же самое, что isLoading очередей: сюда входит и ошибка.
   */
  isBlocking: boolean;
  requiresChoice: boolean;
}

/**
 * Блоки опций товара: и для точек входа СПИСКА, и для карточки.
 *
 * Живёт отдельным хуком, потому что гейт быстрого «+» обязан знать про
 * обязательные блоки ДО открытия карточки. Словари при этом тянутся один
 * раз на всё меню и кэшируются TanStack по точке.
 *
 * У товара без групп опций (optionGroupBinds пуст) словари не запрашиваются
 * вовсе — hasGroups=false отключает оба запроса, и isBlocking остаётся
 * false: такой товар не ждёт словари, которые ему не нужны (спека §4.1: у
 * бренда без единой группы опций не уходит ни одного запроса).
 */
export function useProductOptionBlocks(
  product: MenuProduct,
): UseProductOptionBlocksResult {
  const pointId = useAppSelector(selectPointId);
  const hasGroups = product.optionGroupBinds.length > 0;

  const groupsQuery = useGuestOptionGroups(hasGroups ? pointId : null);
  const optionsQuery = useGuestOptions(hasGroups ? pointId : null);

  const blocks = useMemo(
    () =>
      resolveProductOptions(
        product,
        groupsQuery.data ?? [],
        optionsQuery.data ?? [],
      ),
    [product, groupsQuery.data, optionsQuery.data],
  );

  // ОШИБКА словарей обязана вести себя как «жду», а не как «блоков нет».
  // В TanStack v5 isLoading === isPending && isFetching, то есть на ошибке
  // он false; без isError провалившийся запрос давал бы `data ?? []` →
  // пустые blocks → requiresChoice: false, и товар с обязательным блоком
  // уехал бы телом {productId, quantity} прямо в 422/13222 с откатом.
  const isBlocking =
    hasGroups &&
    (groupsQuery.isLoading ||
      optionsQuery.isLoading ||
      groupsQuery.isError ||
      optionsQuery.isError);

  return { blocks, isBlocking, requiresChoice: hasRequiredBlock(blocks) };
}
